import React, { useState, useEffect, useCallback } from 'react';

interface ScrambleTextProps {
  texts: string[];
  className?: string;
  scrambleDuration?: number;
  displayDuration?: number;
  pauseDuration?: number;
  scrambleChars?: string;
}

const ScrambleText: React.FC<ScrambleTextProps> = ({
  texts,
  className = '',
  scrambleDuration = 1000,     // Slightly longer for better visibility
  displayDuration = 3000,      // Display each text longer
  pauseDuration = 500,         // Slightly longer pause
  scrambleChars = '!@#$%^&*()_+-=[]{}|;:,.<>?~' // More characters for better scrambling
}) => {
  const [currentText, setCurrentText] = useState(texts[0] || '');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isScrambling, setIsScrambling] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const isPaused = false; // Not currently used, but kept for future functionality

  const scrambleToText = useCallback((targetText: string) => {
    if (!targetText) return () => {};
    
    setIsScrambling(true);
    let iterations = 0;
    const maxIterations = Math.max(targetText.length * 1.5, 10); // More iterations for smoother effect
    const intervalTime = Math.max(20, scrambleDuration / maxIterations); // Slightly faster updates

    const interval = setInterval(() => {
      setCurrentText(
        targetText.split('').map((char, index) => {
          if (index < iterations) return char;
          // Only show scramble characters for non-space characters
          return char === ' ' ? ' ' : scrambleChars[Math.floor(Math.random() * scrambleChars.length)];
        }).join('')
      );

      iterations += 1;
      
      if (iterations >= maxIterations) {
        clearInterval(interval);
        setCurrentText(targetText); // Ensure final text is set correctly
        setIsScrambling(false);
      }
    }, intervalTime);

    return () => clearInterval(interval);
  }, [scrambleDuration, scrambleChars]);

  useEffect(() => {
    if (texts.length === 0) return;
    
    let timeoutId: NodeJS.Timeout;
    let isMounted = true;

    const startCycle = async () => {
      if (isPaused || !isMounted) return;
      
      // Initial delay on first render
      if (isFirstRender) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        setIsFirstRender(false);
      }
      
      // Scramble to the current text
      await new Promise(resolve => {
        scrambleToText(texts[currentIndex]);
        setTimeout(resolve, scrambleDuration + displayDuration);
      });
      
      // Only proceed if still mounted
      if (!isMounted) return;
      
      // Move to next text
      setCurrentIndex((prev) => (prev + 1) % texts.length);
      
      // Schedule next cycle
      timeoutId = setTimeout(() => {
        if (isMounted) startCycle();
      }, pauseDuration);
    };

    startCycle();

    // Cleanup
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [currentIndex, isPaused, texts, scrambleToText, displayDuration, scrambleDuration, pauseDuration, isFirstRender]);

  // Handle empty texts array
  if (texts.length === 0) {
    return <span className={className}></span>;
  }

  return (
    <span 
      className={`inline-block min-w-[200px] text-center ${isScrambling ? 'opacity-90' : 'opacity-100'} ${className}`}
      style={{
        transition: 'opacity 0.3s ease-in-out',
        fontVariantLigatures: 'none',
        display: 'inline-block',
        minHeight: '1.2em', // Ensure consistent height
        verticalAlign: 'top', // Prevent layout shift
        fontFamily: 'monospace', // Better for scramble effect
        letterSpacing: '0.05em', // Better character separation
      }}
    >
      {currentText || '\u00A0'}
    </span>
  );
};

export default ScrambleText;